import { useState, useContext, useEffect } from 'react'
import { PassContext } from '../pass-context'
import _ from "lodash"


import { TextField, Typography, Tooltip, Button, FormControlLabel, FormLabel, RadioGroup, Radio } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'

import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'

import dataSelector from './data-selector'
import PopupShell from '../popup-shell'

const PopupDialogBarcode = ({ open, close, path }) => {

  const { classData, setClassData, objectData, instanceData, setObjectData, setInstanceData, syncBarcodeData, setGoogleBoth } = useContext(PassContext)

  const [newClassData, setNewClassData] = useState({})
  const [newInstanceData, setNewInstanceData] = useState({})
  const [barcode, setBarcode] = useState(null)


  // Runs on "open" state change or if object / instanceData change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    try {
      if (open) {
        setNewInstanceData(_.cloneDeep(instanceData))
        setNewClassData(_.cloneDeep(classData))

        if (objectData.barcode) {
          const initialBarcode = _.get(objectData, "barcode")
          setBarcode(initialBarcode)
        }
      }
    }
    catch (err) {
      throw err
    }
  }, [open, path])


  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const handleChangeBarcodeType = value => {
    if (value !== "NONE") setBarcode({
      "alternateText": "{{__barcode__.__text__}}",
      "type": value,
      "value": "{{__barcode__.__value__}}"
    })
    else setBarcode(null)
  }

  const setSecurityAnimation = enable => {
    setNewClassData(prev => {
      let temp = {...prev}
      if (enable) {
        _.set(temp,"securityAnimation.animationType","FOIL_SHIMMER")
      } else {
        _.unset(temp,"securityAnimation")
      }
      return temp
    })
  }

  const instanceFields = {
    value : "__barcode__.__value__",
    alternateText : "__barcode__.__text__"
  }

  const updateBarcode = (field, value) => {
    setNewInstanceData((prev) => { 
      let t = {...prev}
      _.set(t,instanceFields[field],value)
      return t
    })
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  const saveFormData = () => {
    if (!_.isEqual(newClassData, classData) && !_.isEqual(objectData.barcode, barcode)) {
      setGoogleBoth({
        googleClass: newClassData,
        googleObject: {...objectData,barcode}
      })
    } else {
      if (!_.isEqual(newClassData, classData)) setClassData(newClassData)

      if (!_.isEqual(objectData.barcode, barcode)) setObjectData(prev => {
        let temp = {...prev}
        _.set(temp,"barcode",barcode)
        return temp
      })
    }

    if (!_.isEqual(newInstanceData, instanceData)) setInstanceData(newInstanceData)

    if (!_.isEqual(barcode, objectData.barcode)) syncBarcodeData({ source: "google", barcode })
    close()
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////


  try {
    return (
      <PopupShell open={open} onClose={handleClose}>
        <div className="flex flex-col p-2 h-full w-full overflow-x-hidden">

          <div className="flex flex-row justify-between mt-5 w-full">
            <Typography color="secondary" variant="h6">Barcode</Typography>
            <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
          </div>


          <RadioGroup
            value={barcode?.type ?? "NONE"}
            onChange={e => handleChangeBarcodeType(e.target.value)}
          >
            <FormControlLabel value="AZTEC" control={<Radio />} label="Aztec" />
            <FormControlLabel value="QR_CODE" control={<Radio />} label="QR Code" />
            <FormControlLabel value="PDF_417" control={<Radio />} label="PDF 417" />
            <FormControlLabel value="CODE_128" control={<Radio />} label="Code 128" />
            <FormControlLabel value="NONE" control={<Radio />} label="No barcode" />
          </RadioGroup>

          <div className='flex flex-col gap-2'>
            <FormLabel id="barcode_type_label">Barcode Value (message)</FormLabel>
            <DataSourceData barcode={barcode} newInstanceData={newInstanceData} field="value" updateBarcode={updateBarcode} />
          </div>

          <div className='flex flex-col gap-2'>
            <FormLabel id="barcode_type_label">Barcode Serial Number (altText)</FormLabel>
            <DataSourceData barcode={barcode} newInstanceData={newInstanceData} field="alternateText" updateBarcode={updateBarcode} />
          </div>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography>Security animation : Off</Typography>
            <Switch checked={!!newClassData.securityAnimation} onChange={e => setSecurityAnimation(e.target.checked)} />
            <Typography>On</Typography>
          </Stack>

          <div className='fixed bottom-5 w-[460px]'>
            <hr className='mb-5' />
            <div className="flex flex-row w-full justify-end">
              <Button variant="contained" onClick={saveFormData}>Save</Button>
            </div>
          </div>
        </div>
      </PopupShell >
    )
  }
  catch (err) {
    throw err
  }
}


const DataSourceData = ({ barcode, newInstanceData, field, updateBarcode }) => {

  const resolvedInstanceValue = dataSelector({ objectData: barcode, instanceData: newInstanceData, path: field })

  return (
    <>
      <TextField
        value={barcode ? resolvedInstanceValue : ""}
        slotProps={{ inputLabel: { shrink: true } }}
        type="search"
        onChange={e => { updateBarcode(field, e.target.value) }}
        fullWidth
        multiline={field === "value"}
        maxRows={10}        
      />
    </>
  )
}

export default PopupDialogBarcode
